import { isEmpty, isEqual } from 'lodash-es';
import type { MultiValue } from 'react-select';
import SelectComponent from 'react-select';

import type { Nullable, Optional } from '@zen/utils/typescript';

import { ClearIndicator, getMenuList, getMultiValue, getNoOptionsMessage } from '../components';
import DropdownIndicator from '../components/DropdownIndicator';
import LoadingMessage from '../components/LoadingMessage';
import { getAllOptions } from '../helpers';
import { customTheme, multiSelectStyles } from '../select-styles';
import type { CommonSelectProps, Option } from '../types';

interface Props<Value> extends CommonSelectProps<Value> {
  onChange?: (values: Value[]) => void;
  renderMenuInPortal?: boolean;
  value?: Optional<Value[]>;
}

const MultiSelect = <Value extends Nullable<{}>>(props: Props<Value>) => {
  const {
    autoFocus,
    className,
    formatOptionLabel,
    hasError,
    isClearable = true,
    isDisabled,
    isLoading,
    isOptionDisabled,
    inputValue,
    isSearchable,
    shouldFilterResults = true,
    hideSelectedOptions,
    name,
    onChange,
    onInputChange,
    options,
    placeholder,
    dropdownFooterRenderer,
    suggestion,
    onBlur,
    renderMenuInPortal = false,
    value
  } = props;

  const getValue = (): Option<Value>[] => {
    const allOptions: Option<Value>[] = getAllOptions(options);

    return allOptions.filter(
      (option: Option<Value>) => value && value.some((valueData: Value) => isEqual(valueData, option.value))
    );
  };

  const handleChange = (selectedOptions: MultiValue<Option<Value>>): void => {
    const optionValues = selectedOptions ? selectedOptions.map((option) => option.value) : [];

    onChange?.(optionValues);
  };

  const handleInputChange = (query: string) => {
    if (query) {
      onInputChange?.(query);
    }
  };

  const filterOption = shouldFilterResults ? undefined : () => true;

  const formatLabel = (option: Option<Value>, metadata: { context: 'menu' | 'value'; selectValue: readonly Option<Value>[] }) => {
    const isSelected: boolean = !!metadata.selectValue.find((selectedValue) => selectedValue.value === option.value);

    return formatOptionLabel?.(option, isSelected, metadata.context) || option.label;
  };

  return (
    <SelectComponent
      autoFocus={autoFocus}
      className={className}
      closeMenuOnSelect={false}
      components={{
        DropdownIndicator,
        ClearIndicator,
        MenuList: getMenuList(dropdownFooterRenderer),
        MultiValue: getMultiValue<Option<Value>, true>(),
        NoOptionsMessage: getNoOptionsMessage(suggestion)
      }}
      filterOption={filterOption}
      formatOptionLabel={formatLabel}
      hideSelectedOptions={hideSelectedOptions}
      inputId={name}
      inputValue={inputValue}
      isClearable={isClearable}
      isDisabled={isDisabled}
      isLoading={isLoading}
      isMulti={true}
      isOptionDisabled={isOptionDisabled}
      isSearchable={isSearchable}
      loadingMessage={LoadingMessage}
      menuPlacement="auto"
      name={name}
      onBlur={onBlur}
      onChange={handleChange}
      onInputChange={handleInputChange}
      options={options}
      placeholder={placeholder}
      styles={multiSelectStyles(hasError, 'default', isClearable && !isEmpty(getValue()))}
      theme={customTheme}
      value={getValue()}
      {...(renderMenuInPortal ? { menuPortalTarget: document.body } : {})}
    />
  );
};

export type { Props as MultiSelectProps };
export default MultiSelect;
